import { DateTimePicker } from "@mui/x-date-pickers";
import cn from "classnames";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React from "react";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

interface IProps {
  name: string;
  type?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  defaultValue?: string | number;
  required?: boolean;
  [any: string]: any;
}

const FORMAT = "YYYY-MM-DD HH:mm";

export const DateTimeInput = React.forwardRef((props: IProps, ref: any) => {
  const {
    className,
    defaultValue,
    error,
    format = FORMAT,
    label,
    name,
    onBlur,
    onChange,
    openTo = "year",
    required,
    value,
    variant = "outlined",
    ...rest
  } = props;
  const { t } = useTranslation("admin");
  const id = `input-${name}`;
  const rootClasses = cn("marapp-qa-datetime-input", {
    [className]: !!className,
  });

  // Parse input value as UTC to ensure dates from API are displayed correctly
  // The goal is to treat all date/time selections as UTC, not converting from local timezone
  const parseValue = (val: any) => {
    if (!val) return null;

    // If it's already a dayjs object (from form state or user selection)
    // Extract the date/time components and create a UTC dayjs object with those same components
    // This ensures the picker displays the UTC time, not the local equivalent
    if (dayjs.isDayjs(val)) {
      const dateTimeString = val.format(format);
      return dayjs.utc(dateTimeString, format);
    }

    // If it's a string (from API), parse as UTC
    if (typeof val === "string") {
      // Try parsing as UTC first - this handles UTC date strings from the API
      const utcDate = dayjs.utc(val);
      if (utcDate.isValid()) {
        return utcDate;
      }
      // Fallback: if string doesn't parse as UTC, try parsing and then converting to UTC
      const parsed = dayjs(val);
      if (parsed.isValid()) {
        // Extract components and create UTC date
        return dayjs.utc(parsed.format(format), format);
      }
      return null;
    }

    // For numbers (timestamps), parse as UTC
    if (typeof val === "number") {
      return dayjs.utc(val);
    }

    return null;
  };

  const inputValue = parseValue(value || defaultValue);

  // Append "UTC" to the label to make it clear that dates are in UTC
  const displayLabel = label ? `${t(label)} (UTC)` : "UTC";

  return (
    <DateTimePicker
      onChange={(newValue) => {
        if (!newValue) {
          onChange(null);
          return;
        }

        // Extract date/time components from user's selection and treat them as UTC
        // This ensures that when user selects "2024-01-01 12:00", it's stored as UTC, not converted from local timezone
        const dateTimeString = dayjs(newValue).format(format);
        const utcDate = dayjs.utc(dateTimeString, format);

        if (utcDate.isValid()) {
          onChange(utcDate);
        }
      }}
      format={format}
      label={displayLabel}
      onClose={onBlur}
      openTo={openTo}
      value={inputValue}
      slotProps={{
        textField: {
          name: name,
          fullWidth: true,
          required: required,
          variant: variant,
          error: !!error,
          helperText: error && t(error),
          className: rootClasses,
          placeholder: format,
          FormHelperTextProps: {
            className: `marapp-qa-error marapp-qa-error-${id}`,
          },
          onBlur: onBlur,
        },
        field: {
          clearable: true,
        },
      }}
      {...rest}
    />
  );
});
